/**
 * SpecLens: read a specification and the construction drawings, then review what it says the
 * job needs - the Audio Enhancement product for each requirement, what to watch out for, and
 * the RFIs to send - and leave with a takeoff guide and an RFI document.
 *
 * The PDFs are read in this browser. Only the text of the pages worth analysing is sent to the
 * API, nothing is stored on the server, and a project lives in this browser's own storage
 * (speclens-store.ts) with Export and Import for the owner's backups.
 *
 * Its own top-level component rather than a step bolted onto App.tsx - it shares only auth.ts
 * and api.ts with the converter. main.tsx renders it when the path is /speclens.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { userFrom } from "@plan-forge/entra-auth";
import type { SourceDocument } from "@plan-forge/speclens-contract";
import { authHeader, authRequired, initialAuthState, signIn, signOut, type AuthState } from "./auth.ts";
import { ApiError } from "./api.ts";
import { analyzeSpeclens, judgeManufacturer, listManufacturers } from "./speclens-api.ts";
import { asRawPages, readPdfFile, readTextFile } from "./speclens-pdf.ts";
import {
  decisionKey,
  emptyProject,
  exportFileName,
  parseProjectFile,
  projectText,
  newProjectId,
  restoreProject,
  rfiDate,
  type DecisionKind,
  type ProjectSummary,
  type RfiEdit,
  type RfiFields,
  type SpecLensProject,
  type Verdict,
} from "./speclens-project.ts";
import {
  DEFAULT_SPEC_SELECTION,
  defaultSheetSelection,
  locateSections,
  selectSpecPages,
  toDrawingPageText,
  toPageText,
  type LocatedPage,
  type SpecSelection,
} from "./speclens-reader.ts";
import { SpecLensReview } from "./SpecLensReview.tsx";
import { PagesUsed, type DrawingRead, type SpecRead } from "./SpecLensPages.tsx";
import { SavedProjects } from "./SpecLensProjects.tsx";
import { deleteSpeclensProject, listSpeclensProjects, loadSpeclensProject, saveSpeclensProject } from "./speclens-store.ts";
import { SignIn } from "./SignIn.tsx";
import "./speclens.css";

type Phase = "idle" | "reading" | "analyzing";

const PLAIN_TEXT_SECTION = "27 00 00";

function isPdf(file: File): boolean {
  return file.name.toLowerCase().endsWith(".pdf");
}

function download(name: string, type: string, content: BlobPart): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

export function SpecLensApp() {
  const [auth, setAuth] = useState<AuthState>({ kind: authRequired ? "loading" : "disabled" });
  const [project, setProject] = useState<SpecLensProject>(() => emptyProject(newProjectId(), new Date(), ""));
  const [saved, setSaved] = useState<readonly ProjectSummary[]>([]);
  const [specFiles, setSpecFiles] = useState<File[]>([]);
  const [drawingFiles, setDrawingFiles] = useState<File[]>([]);
  const [specs, setSpecs] = useState<readonly SpecRead[]>([]);
  const [drawings, setDrawings] = useState<readonly DrawingRead[]>([]);
  const [selection, setSelection] = useState<SpecSelection>(DEFAULT_SPEC_SELECTION);
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const dirty = useRef(false);

  useEffect(() => {
    document.title = "SpecLens - DesignTools";
  }, []);

  useEffect(() => {
    if (!authRequired) return;
    void initialAuthState().then(setAuth);
  }, []);

  const headers = useCallback(
    () => authHeader(auth, (session) => setAuth({ kind: "signed-in", session, user: userFrom(session) })),
    [auth],
  );

  const signedInName = auth.kind === "signed-in" ? (auth.user.name ?? auth.user.username ?? "") : "";

  const refreshSaved = useCallback(() => {
    void listSpeclensProjects().then(setSaved);
  }, []);

  useEffect(refreshSaved, [refreshSaved]);

  // Autosave: any change to the project after the first edit is written to this browser.
  useEffect(() => {
    if (!dirty.current) return;
    const timer = window.setTimeout(() => {
      void saveSpeclensProject(project).then(refreshSaved);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [project, refreshSaved]);

  const update = useCallback((change: (current: SpecLensProject) => SpecLensProject) => {
    dirty.current = true;
    setProject((current) => change(current));
  }, []);

  useEffect(() => {
    if (signedInName === "") return;
    setProject((current) =>
      current.rfi.from === "" && current.result === null
        ? { ...current, rfi: { ...current.rfi, from: signedInName, signature: signedInName } }
        : current,
    );
  }, [signedInName]);

  const clearFiles = () => {
    setSpecFiles([]);
    setDrawingFiles([]);
    setSpecs([]);
    setDrawings([]);
    setProblem(null);
  };

  const startNew = () => {
    dirty.current = false;
    setProject(emptyProject(newProjectId(), new Date(), signedInName));
    clearFiles();
  };

  const open = async (id: string) => {
    const loaded = await loadSpeclensProject(id);
    if (loaded === null) {
      setProblem("That project could not be opened.");
      return;
    }
    dirty.current = false;
    setProject(loaded);
    clearFiles();
  };

  const importProject = async (file: File) => {
    try {
      const restored = restoreProject(parseProjectFile(await file.text()));
      if (restored === null) throw new Error("not a SpecLens project");
      dirty.current = true;
      setProject(restored);
      clearFiles();
    } catch {
      setProblem("That file is not a SpecLens project export. Use the .txt file from Export project.");
    }
  };

  const readFiles = async () => {
    if (specFiles.length === 0 && drawingFiles.length === 0) {
      setProblem("Add a specification or construction drawings first.");
      return;
    }
    setProblem(null);
    setPhase("reading");
    try {
      const nextSpecs: SpecRead[] = [];
      for (const file of specFiles) {
        setProgress(`Reading ${file.name}`);
        if (isPdf(file)) {
          const pages = await readPdfFile(file, {
            sheets: false,
            onProgress: (done, total) => setProgress(`Reading ${file.name}: page ${done} of ${total}`),
          });
          nextSpecs.push({ filename: file.name, plain: false, pages: locateSections(asRawPages(pages)) });
        } else {
          const pages = await readTextFile(file);
          const located: LocatedPage[] = pages.map((page) => ({
            page: page.page,
            text: page.text,
            section: PLAIN_TEXT_SECTION,
            sectionTitle: file.name,
            sectionPage: 0,
          }));
          nextSpecs.push({ filename: file.name, plain: true, pages: located });
        }
      }

      const nextDrawings: DrawingRead[] = [];
      for (const file of drawingFiles) {
        const pages = await readPdfFile(file, {
          sheets: true,
          onProgress: (done, total) => setProgress(`Reading ${file.name}: page ${done} of ${total}`),
        });
        nextDrawings.push({ filename: file.name, pages, chosen: defaultSheetSelection(pages) });
      }
      setSpecs(nextSpecs);
      setDrawings(nextDrawings);
    } catch (error) {
      setProblem(error instanceof Error ? `A file could not be read: ${error.message}` : "A file could not be read.");
    } finally {
      setPhase("idle");
      setProgress("");
    }
  };

  const analyze = async () => {
    const documents: SourceDocument[] = [
      ...specs.map(
        (spec): SourceDocument => ({
          filename: spec.filename,
          kind: "spec",
          pages: (spec.plain ? spec.pages : selectSpecPages(spec.pages, selection)).map(toPageText),
        }),
      ),
      ...drawings.map(
        (drawing): SourceDocument => ({
          filename: drawing.filename,
          kind: "drawing",
          pages: drawing.pages.filter((page) => drawing.chosen.includes(page.page)).map(toDrawingPageText),
        }),
      ),
    ].filter((document) => document.pages.length > 0);

    if (documents.length === 0) {
      setProblem("No pages are selected to analyse.");
      return;
    }
    setProblem(null);
    setPhase("analyzing");
    const fallback = (specFiles[0]?.name ?? drawingFiles[0]?.name ?? "Untitled project").replace(/\.[^.]+$/, "");
    const name = project.name.trim() === "" ? fallback : project.name.trim();
    try {
      const result = await analyzeSpeclens({ projectName: name, documents }, headers);
      update((current) => ({
        ...current,
        name,
        files: [
          ...specs.map((spec) => ({ name: spec.filename, kind: "spec" as const })),
          ...drawings.map((drawing) => ({ name: drawing.filename, kind: "drawing" as const })),
        ],
        documents,
        result,
      }));
    } catch (error) {
      setProblem(error instanceof ApiError ? error.errors.join("; ") : "The analysis could not be completed.");
    } finally {
      setPhase("idle");
    }
  };

  const reanalyze = async () => {
    if (project.documents.length === 0) return;
    setPhase("analyzing");
    setProblem(null);
    try {
      const result = await analyzeSpeclens({ projectName: project.name, documents: project.documents }, headers);
      update((current) => ({ ...current, result }));
    } catch (error) {
      setProblem(error instanceof ApiError ? error.errors.join("; ") : "The analysis could not be completed.");
    } finally {
      setPhase("idle");
    }
  };

  const [makers, setMakers] = useState<readonly string[]>([]);
  useEffect(() => {
    if (auth.kind !== "signed-in") return;
    void listManufacturers(headers).then((names) => setMakers(names.filter((entry) => entry.verdict === "approved").map((entry) => entry.name)), () => setMakers([]));
  }, [auth.kind, headers]);

  const judgeMaker = async (name: string, verdict: "approved" | "rejected") => {
    setProblem(null);
    try {
      await judgeManufacturer(name, verdict, headers);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.errors.join("; ") : "That maker could not be saved.");
      return;
    }
    setMakers((current) => (verdict === "approved" ? [...current, name] : current.filter((have) => have !== name)));
    // An approved maker changes how the spec reads, so the analysis is redone; a rejection only hides it.
    // Rejecting one already approved takes it back out, and that redoes the analysis as well.
    if (verdict === "approved" || makers.includes(name)) await reanalyze();
    else update((current) => (current.result === null ? current : { ...current, result: { ...current.result, candidates: (current.result.candidates ?? []).filter((candidate) => candidate.name !== name) } }));
  };

  const decide = (kind: DecisionKind, id: string, verdict: Verdict | null, notes: string) => {
    update((current) => {
      const next = { ...current.decisions };
      if (verdict === null) delete next[decisionKey(kind, id)];
      else next[decisionKey(kind, id)] = { verdict, notes };
      return { ...current, decisions: next };
    });
  };

  const editRfi = (id: string, edit: RfiEdit) => {
    update((current) => ({ ...current, rfiEdits: { ...current.rfiEdits, [id]: { ...current.rfiEdits[id], ...edit } } }));
  };

  const setRfiFields = (fields: Partial<RfiFields>) => {
    update((current) => ({ ...current, rfi: { ...current.rfi, ...fields } }));
  };

  if (auth.kind === "loading") return <div className="booting">Checking your sign-in...</div>;
  if (auth.kind === "signed-out") return <SignIn reason={auth.reason} onSignIn={() => void signIn()} />;

  const busy = phase !== "idle";
  const hasFiles = specFiles.length > 0 || drawingFiles.length > 0;
  const hasRead = specs.length > 0 || drawings.length > 0;

  return (
    <main className="sl">
      <header className="sl__header">
        <div>
          <a className="sl__back" href="/">
            &larr; DesignTools
          </a>
          <h1 className="sl__title">AE SpecLens</h1>
          <p className="sl__subtitle">
            Reads the specification and construction drawings on this computer, finds the Audio Enhancement product for each
            requirement, and gives you a takeoff guide and the RFIs to send. Only the text of the pages it analyses leaves
            your browser, and nothing is kept on a server.
          </p>
        </div>
        {auth.kind === "signed-in" && (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              signOut();
              setAuth({ kind: "signed-out" });
            }}
          >
            Sign out
          </button>
        )}
      </header>

      <div className="sl__layout">
        <div className="sl__left">
          <SavedProjects
            projects={saved}
            currentId={project.id}
            onOpen={(id) => void open(id)}
            onNew={startNew}
            onDelete={(id) => {
              void deleteSpeclensProject(id).then(refreshSaved);
              if (id === project.id) startNew();
            }}
          />

          <section className="sl-card">
            <h2 className="sl-card__title">Project</h2>
            <div className="field">
              <label htmlFor="sl-name">Project name</label>
              <input
                id="sl-name"
                type="text"
                value={project.name}
                onChange={(event) => update((current) => ({ ...current, name: event.target.value }))}
                placeholder="FL, Duval SD, Duval ES"
              />
              <p className="hint">State, school district, school.</p>
            </div>

            <FilePicker
              id="sl-spec"
              label="Specification (.pdf, .txt)"
              accept=".pdf,.txt,.md"
              files={specFiles}
              onChange={(files) => {
                setSpecFiles(files);
                setSpecs([]);
              }}
            />
            <FilePicker
              id="sl-drawings"
              label="Construction drawings (.pdf)"
              accept=".pdf"
              files={drawingFiles}
              onChange={(files) => {
                setDrawingFiles(files);
                setDrawings([]);
              }}
            />

            {problem !== null && (
              <p className="sl__problem" role="alert">
                {problem}
              </p>
            )}
            {phase === "reading" && (
              <p className="hint" role="status">
                {progress}
              </p>
            )}

            <button type="button" className="btn btn--primary" disabled={busy || !hasFiles} onClick={() => void readFiles()}>
              {phase === "reading" ? "Reading..." : hasRead ? "Read the files again" : "Read the files"}
            </button>
          </section>

          {hasRead && (
            <PagesUsed
              specs={specs}
              drawings={drawings}
              selection={selection}
              onSelection={setSelection}
              onDrawingChosen={(filename, chosen) =>
                setDrawings((current) => current.map((drawing) => (drawing.filename === filename ? { ...drawing, chosen } : drawing)))
              }
              busy={busy}
              analyzing={phase === "analyzing"}
              onAnalyze={() => void analyze()}
            />
          )}

          {project.result !== null && (
            <section className="sl-card">
              <h2 className="sl-card__title">This project</h2>
              <p className="hint">
                {project.files.map((file) => file.name).join(", ")}. Saved in this browser. Export it to back it up or to open it on
                another computer.
              </p>
              <div className="sl__row">
                <button type="button" className="btn" onClick={() => download(exportFileName(project), "text/plain;charset=utf-8", projectText(project))}>
                  Export project
                </button>
                <button type="button" className="btn" disabled={busy || project.documents.length === 0} onClick={() => void reanalyze()}>
                  {phase === "analyzing" ? "Analyzing..." : "Analyze again"}
                </button>
              </div>
            </section>
          )}

          <section className="sl-card">
            <h2 className="sl-card__title">Import a project</h2>
            <input
              type="file"
              aria-label="Import a project export"
              accept=".txt,.json,text/plain,application/json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) void importProject(file);
                event.target.value = "";
              }}
            />
          </section>
        </div>

        <div className="sl__right">
          {project.result === null ? (
            <section className="sl-card sl-empty">
              <h2 className="sl-card__title">Start here</h2>
              <ol>
                <li>Name the project and choose the specification and the construction drawings.</li>
                <li>Read the files. SpecLens picks the divisions and sheets that matter; you can change them.</li>
                <li>Analyze, review each requirement, then use the takeoff guide and generate the RFI.</li>
              </ol>
            </section>
          ) : (
            <SpecLensReview
              project={project}
              onDecide={decide}
              makers={makers}
              onJudgeMaker={judgeMaker}
              onEditRfi={editRfi}
              onRfiFields={setRfiFields}
              today={rfiDate(new Date())}
            />
          )}
        </div>
      </div>
    </main>
  );
}

interface FilePickerProps {
  readonly id: string;
  readonly label: string;
  readonly accept: string;
  readonly files: readonly File[];
  readonly onChange: (files: File[]) => void;
}

function FilePicker({ id, label, accept, files, onChange }: FilePickerProps) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="file"
        accept={accept}
        multiple
        onChange={(event) => onChange(event.target.files === null ? [] : Array.from(event.target.files))}
      />
      {files.length > 0 && <p className="hint">{files.map((file) => file.name).join(", ")}</p>}
    </div>
  );
}
